/**
 * The HTTP client.
 *
 * Configuration is runtime rather than build-time so the same bundle can be
 * embedded in a host application that knows its own API URL, and so an auth
 * scheme can be added without touching any call site: `configure({ getToken })`
 * is the whole of it. Standalone, the Vite variable still supplies the default.
 */
export interface ApiConfig {
  /** Origin the API is served from, with no trailing slash. */
  baseUrl: string
  /**
   * Returns an Authorization header value for each request, or null for none.
   * Called per request, so a host can refresh a short-lived token freely.
   */
  getAuthorization?: () => string | null | undefined | Promise<string | null | undefined>
  /**
   * Whether to send cookies. Needed by the bundled cookie login; a host that
   * mints its own tokens should turn it off so the call is not a CORS
   * credentialed request.
   */
  credentials?: RequestCredentials
  /** Swapped out in tests, or by a host that proxies through its own origin. */
  fetch?: typeof fetch
  /** Basemaps fetched straight from their provider; see `BasemapUrls`. */
  basemaps?: BasemapUrls
}

/**
 * Basemap tile URLs the browser fetches itself, by style. A style left out
 * comes through the API's tile proxy (`GET /basemap`), which is where a
 * provider's secret key belongs: anything given here is visible to anyone
 * using the page, so give only keyless tiles or a key the provider lets you
 * restrict to your own domains (MapTiler's and Mapbox's public keys).
 *
 * A template with {z}, {x} and {y}, or that with the attribution the
 * provider requires and the deepest zoom it serves.
 */
export type BasemapUrls = Partial<Record<'light' | 'labels' | 'satellite',
  string | { url: string; attribution?: string; maxzoom?: number }>>

const config: ApiConfig = {
  baseUrl: (import.meta.env?.VITE_API_URL as string | undefined) ?? 'http://localhost:4000',
  credentials: 'include',
}

export function configure(next: Partial<ApiConfig>): void {
  Object.assign(config, next)
  if (config.baseUrl.endsWith('/')) config.baseUrl = config.baseUrl.replace(/\/+$/, '')
}

export const getConfig = (): Readonly<ApiConfig> => config

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function parse(res: Response): Promise<unknown> {
  const type = res.headers.get('content-type') ?? ''
  if (type.includes('json')) return res.json()
  if (type.includes('text/')) return res.text()
  return res.blob()
}

/**
 * The last Authorization value resolved, kept so a synchronous caller can use
 * it. MapLibre's transformRequest cannot await, and basemap tiles go through
 * this API — an unauthenticated tile proxy would be a free tile service for
 * anyone who found the URL, billed to us.
 */
let lastAuthorization: string | null = null

async function authHeaders(): Promise<Record<string, string>> {
  const value = await config.getAuthorization?.()
  lastAuthorization = value ?? null
  return value ? { authorization: value } : {}
}

/** The credential for a request that cannot await one. */
export const currentAuthorization = () => lastAuthorization

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const doFetch = config.fetch ?? fetch
  const res = await doFetch(`${config.baseUrl}${path}`, {
    credentials: config.credentials,
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
      ...(await authHeaders()),
      ...init.headers,
    },
  })
  const body = await parse(res)
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${res.status})`
    throw new ApiError(message, res.status, body)
  }
  return body as T
}

export const get = <T,>(path: string) => api<T>(path)
export const post = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
export const patch = <T,>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
export const put = <T,>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) })
export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' })

export function upload<T>(path: string, form: FormData): Promise<T> {
  return api<T>(path, { method: 'POST', body: form })
}

/** Absolute URL for links the browser follows itself, such as downloads. */
export const apiUrl = (path: string) => `${config.baseUrl}${path}`

/**
 * Opens a download.
 *
 * Fetched rather than linked so the request carries whatever credential the
 * deployment uses — a bare anchor would send the cookie but never a bearer
 * token, and the export would 401 in an embedded deployment.
 */
export async function download(path: string, filename: string): Promise<void> {
  const doFetch = config.fetch ?? fetch
  const res = await doFetch(apiUrl(path), {
    credentials: config.credentials,
    headers: await authHeaders(),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    throw new ApiError(body?.error ?? `Export failed (${res.status})`, res.status, body)
  }
  // A large export comes back as a signed link to the file in storage
  // (`delivery=link`). Followed directly, so the browser streams it to disk
  // instead of this page holding the whole file; the link names the file.
  if ((res.headers.get('content-type') ?? '').includes('json')) {
    const body = (await res.json()) as { url?: string }
    if (body.url) return clickDownload(body.url)
  }
  const url = URL.createObjectURL(await res.blob())
  clickDownload(url, filename)
  URL.revokeObjectURL(url)
}

function clickDownload(href: string, filename?: string) {
  const a = document.createElement('a')
  a.href = href
  if (filename) a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}
